import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

declare global {
  interface Window {
    Pusher: typeof Pusher
  }
}

window.Pusher = Pusher

let echoInstance: Echo<'reverb'> | null = null

const toNumber = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function getXsrfTokenFromCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  if (!match) return ''
  try {
    return decodeURIComponent(match[1])
  } catch {
    return match[1]
  }
}

/** Base URL de la API (misma que axios `baseURL`) para cookies de sesión / CSRF. */
function resolveApiBaseUrl(): string {
  const base = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || ''
  if (base.startsWith('http://') || base.startsWith('https://')) {
    return base
  }
  if (base.startsWith('/')) {
    return `${window.location.origin}${base}`
  }
  return window.location.origin
}

function broadcastAuthUrl(): string {
  const path = import.meta.env.VITE_REVERB_AUTH_ENDPOINT || '/broadcasting/auth'
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${resolveApiBaseUrl()}${normalized}`
}

/**
 * Pusher no envía `withCredentials` en el XHR de autorización por defecto;
 * usamos un handler que hace POST con cookies (sesión Sanctum) + CSRF.
 */
function createBroadcastChannelAuthorizer() {
  return (
    params: { socketId: string; channelName: string },
    callback: (error: Error | null, data: unknown) => void,
  ) => {
    const body = new URLSearchParams({
      socket_id: params.socketId,
      channel_name: params.channelName,
    })
    void fetch(broadcastAuthUrl(), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': getXsrfTokenFromCookie(),
      },
      body: body.toString(),
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text()
          throw new Error(
            `Broadcasting auth failed (${res.status}): ${text.slice(0, 200)}`,
          )
        }
        return res.json() as Promise<Record<string, unknown>>
      })
      .then((data) => callback(null, data))
      .catch((err: unknown) => {
        callback(err instanceof Error ? err : new Error(String(err)), null)
      })
  }
}

export const createEchoClient = (): Echo<'reverb'> => {
  if (echoInstance) {
    return echoInstance
  }

  const host = import.meta.env.VITE_REVERB_HOST || window.location.hostname
  const port = toNumber(import.meta.env.VITE_REVERB_PORT, 8080)
  const scheme = import.meta.env.VITE_REVERB_SCHEME || 'http'

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: import.meta.env.VITE_REVERB_APP_KEY,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS: scheme === 'https',
    enabledTransports: ['ws', 'wss'],
    bearerToken: null,
    authEndpoint: broadcastAuthUrl(),
    auth: {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-XSRF-TOKEN': getXsrfTokenFromCookie(),
      },
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tipos pusher-js vs respuesta Laravel
    channelAuthorization: {
      customHandler: createBroadcastChannelAuthorizer(),
    } as any,
  })

  return echoInstance
}

export const getEchoClient = (): Echo<'reverb'> | null => echoInstance

export const disconnectEchoClient = () => {
  if (!echoInstance) return

  echoInstance.disconnect()
  echoInstance = null
}
